import React, { useEffect, useContext, useCallback, useState, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { RootStoreContext } from "src/stores/RootStore";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import {
    GET_MANAGER_FILES_BY_REALESTATEID,
    GET_OWNER_FILES_BY_REALESTATEID,
    GET_COOWNER_FILES_BY_REALESTATEID,
    GET_COOWNER_FILES_BY_REALESTATEID_IT2,
    GET_TENANT_FILES_BY_REALESTATEID,
    GET_TENANT_FILES_BY_REALESTATEID_IT2
} from "src/api/documents";
import { GET_ALL_ASSEMBLIES_BY_COWWNER_REALESTATEID, GET_ASSEMBLIES_BY_REALESTATEID } from "src/api/assembly";
import { useParams, withRouter } from "react-router";
import { getLang } from "src/utils/i18n";
import * as Screens from "src/screens";
import { Route, selectRoute } from "src/config/routes";
import { ScreenType } from "src/api/tiles";
import { ScreenWithTiles } from "src/components/screenWithTiles/ScreenWithTiles";
import { observer } from "mobx-react";
import { ErpType, Role } from "src/network/User";
import { DocumentsList } from "./list/DocumentsList";
import { UI } from "@wwimmo/ui";
import styles from "./Documents.module.css";
import {
    GetCoownerFilesByRealEstateId,
    GetCoownerFilesByRealEstateIdVariables
} from "src/api/generated/GetCoownerFilesByRealEstateId";
import {
    GetAssembliesByRealestateid,
    GetAssembliesByRealestateidVariables
} from "src/api/generated/GetAssembliesByRealestateid";
import {
    GetAllAssembliesCoownerRealestateId,
    GetAllAssembliesCoownerRealestateIdVariables
} from "src/api/generated/GetAllAssembliesCoownerRealestateId";

interface Params {
    realestateid: string;
    ownerId: string;
}

type AssemblyDocumentTitleData = GetAssembliesByRealestateid | GetAllAssembliesCoownerRealestateId;
type AssemblyDocumentTitleVariables =
    | GetAssembliesByRealestateidVariables
    | GetAllAssembliesCoownerRealestateIdVariables;

export const DocumentsBase = () => {
    const { t } = useTranslation();
    const { navStore, authStore, uiStore } = useContext(RootStoreContext);
    const { realestateid } = useParams<Params>();
    const [refetchKey, setRefetchKey] = useState(0);
    const isManager = authStore.user?.role === Role.MANAGER;
    const isCoowner = authStore.user?.role === Role.COOWNER;
    const shouldLoadAssemblyDocumentTitles = isManager || isCoowner;
    const assemblyDocumentTitlesQuery = isManager
        ? GET_ASSEMBLIES_BY_REALESTATEID
        : GET_ALL_ASSEMBLIES_BY_COWWNER_REALESTATEID;

    useEffect(() => {
        if (navStore.currentOwnerId) {
            if (navStore.singleRealestate) {
                navStore.setBackbutton(
                    selectRoute(Route.owners, authStore.portalRoleWithHighestPriority ?? authStore.user?.role),
                    t("screens.owners.title")
                );
            } else {
                navStore.setBackbutton(
                    selectRoute(
                        Route.realestatesOwner,
                        authStore.portalRoleWithHighestPriority ?? authStore.user?.role,
                        { ownerid: navStore.currentOwnerId }
                    ),
                    t("screens.realestates.title")
                );
            }
        } else {
            navStore.setBackbutton(
                selectRoute(Route.realestates, authStore.portalRoleWithHighestPriority ?? authStore.user?.role),
                t("screens.realestates.title")
            );
        }
    }, [authStore.portalRoleWithHighestPriority, authStore.user?.role, t, navStore, uiStore.isMobile]);

    const { loading, error, data, refetch } = useQuery<
        GetCoownerFilesByRealEstateId,
        GetCoownerFilesByRealEstateIdVariables
    >(
        authStore.user?.role === Role.MANAGER
            ? GET_MANAGER_FILES_BY_REALESTATEID
            : authStore.user?.role === Role.OWNER
            ? GET_OWNER_FILES_BY_REALESTATEID
            : authStore.user?.role === Role.TENANT && authStore.user.erpType === ErpType.IT2
            ? GET_TENANT_FILES_BY_REALESTATEID_IT2
            : authStore.user?.role === Role.TENANT
            ? GET_TENANT_FILES_BY_REALESTATEID
            : authStore.user?.hasCoownerRole && authStore.user.erpType === ErpType.IT2
            ? GET_COOWNER_FILES_BY_REALESTATEID_IT2
            : GET_COOWNER_FILES_BY_REALESTATEID,

        {
            variables: {
                realestateid: realestateid,
                lang: getLang()
            },
            fetchPolicy: "no-cache"
        }
    );

    const { data: assemblyData } = useQuery<AssemblyDocumentTitleData, AssemblyDocumentTitleVariables>(
        assemblyDocumentTitlesQuery,
        {
            variables: {
                realestateid: realestateid
            },
            fetchPolicy: "no-cache",
            skip: !shouldLoadAssemblyDocumentTitles
        }
    );

    const handleDocumentOpened = useCallback(() => {
        refetch().then(() => {
            setRefetchKey((prev: number) => prev + 1);
        });
    }, [refetch]);

    const fileCategories = useMemo(() => {
        if (!data) return [];
        const seen = new Set<string>();
        const counts = new Map<string, number>();
        data.realestates.forEach((realestate) => {
            realestate.files.forEach((file) => {
                if (file?.viewed === false) {
                    const id = file.fileid ?? file.id;
                    if (!id || seen.has(id)) return;
                    seen.add(id);
                    const catKey = file.category ?? "unknown";
                    counts.set(catKey, (counts.get(catKey) || 0) + 1);
                }
            });
        });
        return (data.file_categories || []).map((fileCategory) => ({
            ...fileCategory,
            count: counts.get(fileCategory.key) || 0
        }));
    }, [data]);

    const assemblyDocumentTitles = useMemo(() => {
        return (
            assemblyData?.assemblyitems.map((assemblyItem) => ({
                title: assemblyItem.title,
                date: assemblyItem.date
            })) ?? []
        );
    }, [assemblyData]);

    if (loading) return <Screens.Loading />;
    if (error) return <Screens.Error message={error.message} networkError={error.networkError} />;
    if (!data) return <Screens.Error message={t("error.nodata")} />;

    const hasDocuments = data.realestates.some((realestate) => realestate.files.length > 0);

    return (
        <ScreenWithTiles
            title={t("screens.realestate.documents.title")}
            screenId={ScreenType.DOCUMENTS}
            realestateid={realestateid}
        >
            <UI.Card flex={true}>
                {hasDocuments ? (
                    <DocumentsList
                        key={refetchKey}
                        realestateFileItems={data.realestates}
                        fileCategories={fileCategories}
                        assemblyDocumentTitles={assemblyDocumentTitles}
                        onDocumentOpened={handleDocumentOpened}
                    />
                ) : (
                    <div className={styles.noData}>{t("screens.realestate.documents.no_documents")}</div>
                )}
            </UI.Card>
        </ScreenWithTiles>
    );
};

export const Documents = Sentry.withProfiler(withRouter(observer(DocumentsBase)));
